import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { FileText, Lock, Calendar, Users, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.background.primary};
`;

const Content = styled.div`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 3rem 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const PageDescription = styled.p`
  font-size: 1.125rem;
  color: ${props => props.theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
`;

const FormsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const FormCard = styled(Card)`
  padding: 2rem;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const FormTypeBadge = styled.span`
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.type === 'public' 
    ? props.theme.colors.primary + '20' 
    : props.theme.colors.warning + '20'};
  color: ${props => props.type === 'public' 
    ? props.theme.colors.primary 
    : props.theme.colors.warning};
  margin-bottom: 1rem;
`;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const FormDescription = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const FormMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: ${props => props.theme.colors.background.secondary};
  border-radius: 8px;
  font-size: 0.875rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'open') return props.theme.colors.success + '20';
    if (props.status === 'closed') return props.theme.colors.error + '20';
    return props.theme.colors.gray[100];
  }};
  color: ${props => {
    if (props.status === 'open') return props.theme.colors.success;
    if (props.status === 'closed') return props.theme.colors.error;
    return props.theme.colors.text.secondary;
  }};
`;

const ClosedMessage = styled.div`
  text-align: center;
  padding: 2rem;
  background: ${props => props.theme.colors.error}10;
  border-radius: 8px;
  margin-top: 1rem;
`;

const ClosedIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.theme.colors.error}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: ${props => props.theme.colors.error};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const PublicMembershipApplicationsList = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [publicForms, setPublicForms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const publicRes = await membershipApplicationAPI.getPublicForms();
      setPublicForms(publicRes.data || []);
    } catch (error) {
      toast.error('Failed to load application forms');
    } finally {
      setLoading(false);
    }
  };

  const getFormStatus = (form) => {
    if (!form.is_active) return 'inactive';
    const now = new Date();
    if (form.opens_at && new Date(form.opens_at) > now) return 'scheduled';
    if (form.closes_at && new Date(form.closes_at) < now) return 'closed';
    return 'open';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderFormCard = (form) => {
    const status = getFormStatus(form);
    const isOpen = status === 'open';

    return (
      <FormCard key={form.id} theme={theme}>
        <FormTypeBadge type={form.type} theme={theme}>
          Membership Application
        </FormTypeBadge>
        <FormTitle theme={theme}>{form.title}</FormTitle>
        {form.description && (
          <FormDescription theme={theme}>{form.description}</FormDescription>
        )}
        
        <FormMeta theme={theme}>
          <MetaItem theme={theme}>
            <Calendar size={16} />
            <span><strong>Opens:</strong> {formatDate(form.opens_at)}</span>
          </MetaItem>
          <MetaItem theme={theme}>
            <Calendar size={16} />
            <span><strong>Closes:</strong> {formatDate(form.closes_at)}</span>
          </MetaItem>
          <MetaItem theme={theme}>
            <FileText size={16} />
            <span><strong>Fields:</strong> {(form.activeFields || form.active_fields || []).length}</span>
          </MetaItem>
        </FormMeta>

        <StatusBadge status={status} theme={theme}>
          {status === 'open' && '✓ Open for Applications'}
          {status === 'closed' && '✗ Closed'}
          {status === 'scheduled' && '⏰ Scheduled'}
          {status === 'inactive' && '○ Inactive'}
        </StatusBadge>

        {!isOpen && (
          <ClosedMessage theme={theme}>
            <ClosedIcon theme={theme}>
              <Lock size={24} />
            </ClosedIcon>
            <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>
              Application Closed
            </div>
            <div style={{ fontSize: '0.875rem' }}>
              {form.closed_message || 'This application period has ended.'}
            </div>
          </ClosedMessage>
        )}

        {isOpen && (
          <Button
            variant="primary"
            onClick={() => navigate(`/membership-application/${form.slug}`)}
            style={{ width: '100%', marginTop: '1rem' }}
            leftIcon={<FileText size={18} />}
          >
            Apply Now
          </Button>
        )}
      </FormCard>
    );
  };

  return (
    <Container theme={theme}>
      <PublicHeader theme={theme} />
      <Content>
        <PageHeader>
          <PageTitle theme={theme}>Membership Applications</PageTitle>
          <PageDescription theme={theme}>
            Apply to become a member of the Ethiopian Women Human Rights Network (EWHRN). Fill out the application form below.
          </PageDescription>
        </PageHeader>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>Loading applications...</div>
        ) : publicForms.length === 0 ? (
          <EmptyState theme={theme}>
            <FileText size={64} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <h3>No applications available</h3>
            <p>There are currently no membership application forms open for submission.</p>
          </EmptyState>
        ) : (
          <FormsGrid>
            {publicForms.map(renderFormCard)}
          </FormsGrid>
        )}
      </Content>
      <PublicFooter theme={theme} />
    </Container>
  );
};

export default PublicMembershipApplicationsList;


